import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, UserCheck, X } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminNetworks() {
  const { t } = useI18n();
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [networks, setNetworks] = useState([]);
  const [pickNet, setPickNet] = useState(null); // network to impersonate from
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/networks").then((r) => setNetworks(r.data));
  }, []);

  const openImpersona = async (net) => {
    setPickNet(net);
    const r = await api.get(`/superadmin/users?network_id=${net.id}`);
    setUsers(r.data || []);
  };

  const doImpersona = async (u) => {
    setPickNet(null);
    const res = await impersonate(u.id);
    if (!res.success) {
      toast.error(res.error || "Impossibile impersonare l'utente");
      return;
    }
    const role = res.data?.role || u.role;
    const dest = role === "casa_madre" ? "/casa-madre" : role === "rivenditore" ? "/rivenditore" : "/account";
    // Hard navigation to guarantee fresh React tree + new token applied everywhere
    window.location.href = dest;
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="sa-networks-page">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline text-brand mb-2">SuperAdmin</div>
            <h1 className="font-heading font-bold text-4xl tracking-tight">{t("sa_networks")}</h1>
          </div>
          <Link to="/superadmin/networks/new" data-testid="sa-networks-create-btn"
            className="text-sm px-4 h-10 inline-flex items-center rounded-sm bg-brand hover:bg-brand-hover text-white transition-colors">
            + {t("sa_create_network")}
          </Link>
        </div>

        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-border">
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Network</th>
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Vertical</th>
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Domain</th>
                <th className="text-center px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Tenants</th>
                <th className="text-center px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Prodotti</th>
                <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {networks.map((n) => (
                <tr key={n.id} className="border-b border-border hover:bg-accent/30" data-testid={`net-row-${n.network_key}`}>
                  <td className="px-5 py-4">
                    <div className="font-heading font-bold">{n.display_name}</div>
                    <div className="text-xs text-muted-foreground">{n.network_key}</div>
                  </td>
                  <td className="px-5 py-4 text-sm font-mono">{n.vertical_template_used}</td>
                  <td className="px-5 py-4 text-sm">{n.main_domain}</td>
                  <td className="px-5 py-4 text-center font-medium">{n.stats?.total_tenants || 0}</td>
                  <td className="px-5 py-4 text-center font-medium">{n.stats?.total_prodotti || 0}</td>
                  <td className="px-5 py-4">
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{n.status}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openImpersona(n)} data-testid={`net-impersona-${n.network_key}`}
                        title="Impersona un utente del network"
                        className="text-xs px-3 h-8 rounded-sm border border-border hover:bg-foreground hover:text-white hover:border-foreground inline-flex items-center gap-1.5 transition-colors">
                        <UserCheck className="w-3.5 h-3.5" /> Impersona
                      </button>
                      <Link to={`/superadmin/networks/${n.id}`} data-testid={`net-detail-link-${n.network_key}`}
                        className="text-brand text-sm font-medium inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
                        Apri <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pickNet && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setPickNet(null)} data-testid="impersona-picker-modal">
            <div className="bg-white rounded-sm w-full max-w-lg max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <div>
                  <div className="overline text-brand">Impersona</div>
                  <h3 className="font-heading font-bold text-lg">{pickNet.display_name}</h3>
                </div>
                <button onClick={() => setPickNet(null)} className="w-8 h-8 rounded-sm hover:bg-accent"><X className="w-4 h-4 mx-auto" /></button>
              </div>
              <div className="p-3 space-y-1">
                {users.length === 0 ? <div className="text-sm text-muted-foreground p-4 text-center">Nessun utente trovato</div> :
                  users.map((u) => (
                    <button key={u.id} onClick={() => doImpersona(u)} data-testid={`impersona-user-${u.email}`}
                      className="w-full p-3 rounded-sm border border-border hover:border-foreground hover:bg-accent text-left transition-all flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{u.name || u.email}</div>
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      </div>
                      <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-sm ${u.role === "casa_madre" ? "bg-amber-100 text-amber-800" : u.role === "rivenditore" ? "bg-blue-100 text-blue-800" : "bg-neutral-100 text-neutral-700"}`}>{u.role}</span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
